import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

import ClinicPage from '../pages/ClinicPage';
import ClinicPatients from '../pages/ClinicPatients';
import ClinicPatient from '../pages/ClinicPatient';
import ErrorPage from '../pages/ErrorPage';
import ClinicMaterials from '../pages/ClinicMaterials';
import ClinicCalendar from '../pages/ClinicCalendar';
import Loader from '../components/Loader';
import ClinicMaterialsProtocols from '../pages/ClinicMaterialsProtocols';
import ClinicMaterialsContracts from '../pages/ClinicMaterialsContracts';
import ClinicMaterialsForms from '../pages/ClinicMaterialsForms';
import ClinicMaterialsForm from '../pages/ClinicMaterialsForm';
import ClinicPatientForm from '../pages/ClinicPatientForm';
import ClinicPatientFormResponse from '../pages/ClinicPatientFormResponse';
import ClinicPatientContracts from '../pages/ClinicPatientContracts';
import ClinicPatientPlanosAlimentares from '../pages/ClinicPatientPlanosAlimentares';
import ClinicPatientPlanoAlimentar from '../pages/ClinicPatientPlanoAlimentar';
import ClinicPatientConsults from '../pages/ClinicPatientConsults';
import ClinicPatientConsult from '../pages/ClinicPatientConsult';
import EditBlogPostPage from '../pages/EditBlogPostPage';
import EditBlogPostsPage from '../pages/EditBlogPostsPage';
import Sidebar from '../components/Sidebar';
import styles from './ClinicRoutes.module.scss';
import BottomBar from '../components/BottomBar';

import { FaCalendarAlt, FaUserFriends, FaFileAlt, FaClipboardList, FaBlog } from "react-icons/fa";

const CalendarIcon = FaCalendarAlt as React.FC<{ size?: number; color?: string }>;
const UserFriendsIcon = FaUserFriends as React.FC<{ size?: number; color?: string }>;
const FileIcon = FaFileAlt as React.FC<{ size?: number; color?: string }>;
const ClipboardListIcon = FaClipboardList as React.FC<{ size?: number; color?: string }>;
const BlogIcon = FaBlog as React.FC<{ size?: number; color?: string }>;

interface Props {
  user: User | null;
  permissions: Permission[];
  loading: boolean;
}

const ClinicRoutes: React.FC<Props> = ({ user, permissions, loading }) => {
  // Aqui você pode adicionar validações com base em permissões se quiser
  const isAuthenticated = !!user;
  const location = useLocation();

  if (loading) return <Loader />;

  if (!isAuthenticated) {
    return <Navigate to={`/login?continue=${encodeURIComponent(location.pathname)}`} />;
  }

  if (!permissions.some(permission => permission.name === 'Profissional')) {
    return <ErrorPage code={403} />;
  }

  // Remove a primeira barra e divide o path em segmentos
  const pathSegments = location.pathname.replace(/^\/+/, "").split("/");

  // Mostra o sidebar apenas se tiver 1 ou 2 segmentos (nível 1 ou 2)
  const showSidebar = pathSegments.length <= 2 && location.pathname !== "/404";


  return (
    <div className={styles.container}>
      {/* Sidebar fixa à esquerda */}
      {showSidebar && (
        <div className={styles.sidebar}>
          <Sidebar menus={[
            {
              key: "dashboard",
              title: "Atendimento",
              icon: <CalendarIcon />,
              items: [
                { label: "Agenda", href: "/consultorio/agenda", icon: <CalendarIcon /> },
                { label: "Pacientes", href: "/consultorio/pacientes", icon: <UserFriendsIcon /> },
              ],
            },
            /*{
              key: "gestao",
              title: "Gestão",
              items: [
                { label: "Materiais", href: "/consultorio" },
              ],
            },*/
            {
              key: "materiais",
              title: "Materiais",
              icon: <FileIcon />,
              items: [
                { label: "Formulários", href: "/consultorio/formularios", icon: <ClipboardListIcon /> },
                { label: "Contratos", href: "/consultorio/contratos", icon: <ClipboardListIcon /> },
                { label: "Protocolos", href: "/consultorio/protocolos", icon: <ClipboardListIcon /> },
              ]
            },
            {
              key: "Comunicacao",
              title: "Comunicação",
              icon: <BlogIcon />,
              items: [
                { label: "Blog", href: "/consultorio/blog-posts", icon: <BlogIcon /> },
              ]
            },
          ]}
            loading={loading}
          />
        </div>
      )}

      {/* Área de conteúdo à direita */}
      <div className={styles.content}>
        <Routes>
          <Route path="/" element={<ClinicPage user={user} />} />

          <Route path="/materiais" element={<ClinicMaterials />} />
          <Route path="/formularios" element={<ClinicMaterialsForms />} />
          <Route path="/formularios/:id" element={<ClinicMaterialsForm />} />
          <Route path="/contratos" element={<ClinicMaterialsContracts />} />
          <Route path="/protocolos" element={<ClinicMaterialsProtocols />} />
          <Route path="/blog-posts" element={<EditBlogPostsPage />} />
          <Route path="/blog-post/:id" element={<EditBlogPostPage />} />

          {/* Rota para o calendário da clínica */}

          <Route path="/agenda" element={<ClinicCalendar />} />

          <Route path="/pacientes" element={<ClinicPatients />} />
          <Route path="/pacientes/:id" element={<ClinicPatient />} />
          <Route path="/pacientes/:id/formularios" element={<ClinicPatientForm />} />
          <Route path="/pacientes/:id/formularios/:formId" element={<ClinicPatientFormResponse />} />
          <Route path="/pacientes/:id/contratos" element={<ClinicPatientContracts />} />
          <Route path="/pacientes/:id/planos-alimentares" element={<ClinicPatientPlanosAlimentares />} />
          <Route path="/pacientes/:id/planos-alimentares/:planoAlimentarId?" element={<ClinicPatientPlanoAlimentar />} />
          <Route path="/pacientes/:id/consultas" element={<ClinicPatientConsults />} />
          <Route path="/pacientes/:id/consultas/:consultaId" element={<ClinicPatientConsult />} />

          {/* Rota padrão para redirecionar para a página inicial */}

          {/* Rota fallback para páginas não encontradas */}
          <Route path="*" element={<ErrorPage code={404} />} />
        </Routes>
      </div>
      {/* Barra inferior para navegação em dispositivos móveis */}
      {showSidebar &&
        <BottomBar
          menu={[
            { label: "Agenda", href: "/consultorio/agenda", icon: <CalendarIcon /> },
            { label: "Pacientes", href: "/consultorio/pacientes", icon: <UserFriendsIcon /> },
            { label: "Materiais", href: "/consultorio/materiais", icon: <FileIcon /> },
            { label: "Blog", href: "/consultorio/blog-posts", icon: <BlogIcon /> },
          ]}
        />
      }
    </div>
  );
};

export default ClinicRoutes;
